import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Card,
  CardBody,
  Input,
} from 'reactstrap';

import ActionButton from '../../../../../Components/Common/ActionButton';
import {
  FormGrid,
  RequiredNote,
  TextField,
} from '../../../../../Components/Form';
import { PARTICIPATION, isValidEmail } from '../invitationInternalConstants';

// Confirm popup for Invitation - Internal (DOD §Button Confirm).
//
//  - Option box: Present / Delegate.
//  - When Delegate is chosen, a Participant Information card appears with Name
//    (text) and Email (text, email-format validated).
//  - Submit -> onConfirm({ mode, name, email }); the parent flips status to
//    Confirmed and stores the participant.
//  - Cancel -> onClose; nothing is saved.
const ConfirmParticipationModal = ({
  show,
  submitting,
  onConfirm,
  onClose,
}) => {
  const [mode, setMode] = useState(PARTICIPATION.PRESENT);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);

  // Reset the form every time the popup opens so a previous attempt never leaks.
  useEffect(() => {
    if (show) {
      setMode(PARTICIPATION.PRESENT);
      setName('');
      setEmail('');
      setTouched({});
      setSubmitted(false);
    }
  }, [show]);

  // An error shows once the field was left, or after a failed submit.
  const showError = field => submitted || touched[field];
  const touch = field => setTouched(prev => ({ ...prev, [field]: true }));

  const isDelegate = mode === PARTICIPATION.DELEGATE;
  const nameInvalid = isDelegate && !name.trim();
  const emailInvalid = isDelegate && !isValidEmail(email);

  const handleSubmit = () => {
    if (isDelegate && (nameInvalid || emailInvalid)) {
      setSubmitted(true);
      return;
    }
    onConfirm({ mode, name: name.trim(), email: email.trim() });
  };

  return (
    <Modal isOpen={show} toggle={onClose} centered data-bs-backdrop='static'>
      <ModalHeader toggle={onClose}>Confirm Attendance</ModalHeader>
      <ModalBody>
        <p className='text-muted fs-13 mb-3'>
          Confirm your attendance for this tender-opening session.
        </p>

        {/* Option box: Present / Delegate */}
        <div className='d-flex gap-2 mb-3'>
          {[PARTICIPATION.PRESENT, PARTICIPATION.DELEGATE].map(opt => (
            <div
              key={opt}
              role='button'
              className={`flex-grow-1 border rounded p-3 text-center ${
                mode === opt ? 'border-primary bg-soft-primary' : ''
              }`}
              onClick={() => setMode(opt)}
            >
              <div className='form-check d-flex justify-content-center mb-1'>
                <Input
                  type='radio'
                  name='participation-mode'
                  className='form-check-input'
                  checked={mode === opt}
                  onChange={() => setMode(opt)}
                />
              </div>
              <span className='fw-semibold'>{opt}</span>
            </div>
          ))}
        </div>

        {/* Participant information — Delegate only */}
        {isDelegate && (
          <Card className='border shadow-none mb-0'>
            <CardBody>
              <h6 className='fw-semibold mb-3'>Participant Information</h6>
              <RequiredNote />
              <FormGrid columns={1}>
                <TextField
                  label='Name'
                  name='participantName'
                  required
                  placeholder='e.g. Budi Santoso'
                  value={name}
                  error={
                    showError('name') && nameInvalid
                      ? 'Name is required'
                      : undefined
                  }
                  onChange={e => setName(e.target.value)}
                  onBlur={() => touch('name')}
                />
                <TextField
                  label='Email'
                  name='participantEmail'
                  type='email'
                  required
                  placeholder='name@company.com'
                  value={email}
                  error={
                    showError('email') && emailInvalid
                      ? 'Enter a valid email address'
                      : undefined
                  }
                  onChange={e => setEmail(e.target.value)}
                  onBlur={() => touch('email')}
                />
              </FormGrid>
            </CardBody>
          </Card>
        )}
      </ModalBody>
      <ModalFooter>
        <ActionButton
          variant='secondary'
          icon='ri-close-line'
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </ActionButton>
        <ActionButton
          variant='primary'
          icon='ri-send-plane-line'
          onClick={handleSubmit}
          disabled={submitting}
        >
          Submit
        </ActionButton>
      </ModalFooter>
    </Modal>
  );
};

export default ConfirmParticipationModal;
